import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad gör en manusförfattare för film?",
    a: "En manusförfattare översätter ett mål och en målgrupp till en berättelse som går att spela in: kärnidé, struktur, scenlista och färdig text — speakertext, dialog eller intervjufrågor beroende på format.",
  },
  {
    q: "Kan vi hyra in en manusförfattare för ett enskilt uppdrag?",
    a: "Ja. Vi tar fristående manusuppdrag där ni får manus, scenlista och produktionsnoteringar, oavsett vem som sedan spelar in filmen.",
  },
  {
    q: "Vad kostar en manusförfattare?",
    a: "Uppdragspris är vanligast: 8 000–20 000 kr för en företagsfilm, 25 000–75 000 kr för reklamfilm med koncept. Vid längre samarbeten ingår manus i abonnemang från 14 995 kr/mån. Vi debiterar inte per timme.",
  },
  {
    q: "Skriver ni manus till reklamfilm och företagsfilm?",
    a: "Båda, plus employer branding, produktfilm, utbildningsfilm, animation och intervjubaserad dokumentär. Det är olika hantverk: reklam kräver hook och koncept, utbildningsfilm kräver pedagogisk struktur.",
  },
  {
    q: "Använder ni AI när ni skriver manus?",
    a: "Vi använder AI som stöd för research och variantgenerering, men manus, idé och slutlig text är skrivna och redigerade av människor. Kunden får alltid veta vad som är maskingenererat.",
  },
  {
    q: "Hur många revideringar ingår?",
    a: "Två revideringsrundor ingår i uppdragspriset. Fler rundor prissätts separat — men i praktiken räcker två när briefen och godkännandekedjan är tydliga från början.",
  },
];

const TITLE = "Manusförfattare för film och reklamfilm | Fenny";
const DESCRIPTION =
  "Hyr in manusförfattare för reklamfilm, företagsfilm och kampanj. Fristående manusuppdrag eller som del av produktion. Uppdragspris, inga timarvoden.";

export const Route = createFileRoute("/manusforfattare")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Manusförfattare för film — Fenny" },
      { property: "og:description", content: "Manus till reklamfilm, företagsfilm och kampanj — till fast uppdragspris." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/manusforfattare" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/manusforfattare" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: "/manusforfattare", name: TITLE, description: DESCRIPTION })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Manusförfattare för film",
            path: "/manusforfattare",
            serviceType: "Manusförfattande",
            description: "Manusförfattare för reklamfilm, företagsfilm, employer branding och animation — fristående eller i produktion.",
            priceRange: "8 000–75 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Manus och koncept", path: "/manus-och-koncept" },
            { name: "Manusförfattare", path: "/manusforfattare" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Manusförfattare"
        title={<>Manusförfattare som också vet hur filmen spelas in.</>}
        lede="Vi skriver manus till reklamfilm, företagsfilm, employer branding och animation. Skillnaden mot en frilansande skribent: manuset skrivs av ett team som också regisserar och klipper, så det som står i texten går att producera inom budget och tidplan."
        tldr={
          <p>
            En manusförfattare för film tar fram kärnidé, struktur, scenlista och
            färdig text. Fenny arbetar med <strong>fast uppdragspris</strong>:{" "}
            <strong>8 000–20 000 kr</strong> för företagsfilm och{" "}
            <strong>25 000–75 000 kr</strong> för reklamfilm med koncept. Två
            revideringar ingår. Manus kan köpas fristående — ni behöver inte
            beställa produktionen av oss.
          </p>
        }
        ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
        ctaSecondary={{ label: "Manus och koncept", to: "/manus-och-koncept" }}
        trackingId="manusforfattare"
        bullets={[
          { title: "Fast uppdragspris", body: "Inga timarvoden och ingen räknare som tickar under revideringarna." },
          { title: "Skrivet för produktion", body: "Varje scen är prövad mot budget, tid och vad som faktiskt går att filma." },
          { title: "Fristående uppdrag", body: "Ni äger manuset och kan producera var ni vill." },
        ]}
        pricing={{
          heading: "Vad kostar en manusförfattare?",
          intro: "Riktpriser exklusive moms, Sverige 2026.",
          rows: [
            { format: "Kortfilm/social", scope: "15–60 sek, hook, speakertext, 2 revideringar", price: "4 000–9 000 kr", lead: "3–5 dagar" },
            { format: "Företagsfilm", scope: "Kärnidé, scenlista, speaker/intervjuguide", price: "8 000–20 000 kr", lead: "1–2 veckor" },
            { format: "Reklamfilm", scope: "Koncept, 2–3 idéspår, manus, kanalversioner", price: "25 000–75 000 kr", lead: "2–4 veckor" },
            { format: "Manus i abonnemang", scope: "Löpande manus och innehållsplan", price: "ingår från 14 995 kr/mån", lead: "löpande" },
          ],
        }}
        sections={[
          {
            id: "format",
            heading: "Format vi skriver för",
            body: (
              <>
                <p>
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilmsmanus">Reklamfilm</a>{" "}
                  där hooken avgör allt.{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foretagsfilm">Företagsfilm</a>{" "}
                  där trovärdighet väger tyngre än effekt.{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/employer-branding-film">Employer branding</a>{" "}
                  där tonen måste kännas som verkligheten på arbetsplatsen.
                </p>
                <p>
                  Dessutom{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/utbildningsfilm">utbildningsfilm</a>{" "}
                  med pedagogisk struktur,{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-forklaringsfilm">animerad förklaringsfilm</a>{" "}
                  där manus och bild måste skrivas ihop, och intervjubaserad film
                  där "manuset" är en frågeguide och en klippstruktur.
                </p>
              </>
            ),
          },
          {
            id: "sa-skriver-vi",
            heading: "Så ser ett manus från oss ut",
            body: (
              <p>
                Tvåkolumnsformat: bild till vänster, ljud till höger. Varje scen
                har längd i sekunder och total speltid summeras. Speakertext
                räknas mot 130–150 ord per minut så att filmen håller sin längd
                redan i manus. Produktionsnoteringar i marginalen anger vad varje
                scen kräver — location, medverkande, rekvisita, tillstånd.
              </p>
            ),
          },
          {
            id: "brief",
            heading: "Vad vi behöver från er",
            body: (
              <p>
                Mål, målgrupp, kanal och önskad längd. Ett exempel på film ni
                gillar och ett ni ogillar. Vem som godkänner, och när. Det räcker
                — resten gräver vi fram i ett kort briefsamtal på 30 minuter.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        caseLimit={4}
        riskReducers
        closing={{
          title: "Behöver ni ett manus?",
          body: "Berätta målet så återkommer vi med upplägg, pris och tidplan.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["manus", "process", "reklamfilm", "foretagsfilm"]} />
    </>
  );
}
